import axios from 'axios';

const API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
// Using Gemini Flash Latest which ensures we use the best available flash model
const API_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-flash-latest:generateContent';

/**
 * Helper to retry an async function with exponential backoff
 */
const retryOperation = async (operation, retries = 3, delay = 1000) => {
  try {
    return await operation();
  } catch (error) {
    if (retries <= 0) throw error;
    
    // Check if it's a transient error (503 Service Unavailable or 429 Too Many Requests)
    if (error.response && (error.response.status === 503 || error.response.status === 429)) {
      console.log(`Retrying AI request... Attempts left: ${retries}. Waiting ${delay}ms.`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryOperation(operation, retries - 1, delay * 2);
    }
    
    throw error;
  }
};

/**
 * Generates text using Google Gemini API or falls back to simulation if no key is provided.
 * @param {string} topic - The topic to generate text about.
 * @param {string} recipientName - The name of the recipient (User or App name).
 * @returns {Promise<string>} - The generated text.
 */
export const generateAlertText = async (topic, recipientName) => {
  // Fallback to simulation if no API Key is configured
  if (!API_KEY) {
    console.warn('REACT_APP_GEMINI_API_KEY not found in environment variables. Using simulation.');
    return new Promise((resolve) => {
      setTimeout(() => {
        const simulatedText = `Prezado(a) ${recipientName || 'Cliente'},\n\nReferente ao assunto "${topic}", gostaríamos de informar que analisamos sua solicitação e estamos processando as informações necessárias.\n\nCaso tenha dúvidas adicionais, não hesite em nos contatar.\n\nAtenciosamente,\nEquipe de Suporte.`;
        resolve(simulatedText);
      }, 1500);
    });
  }

  try {
    const prompt = `Escreva APENAS o corpo de uma mensagem formal e profissional de alerta/notificação administrativa para um usuário chamado "${recipientName || 'Usuário'}".
    
    O assunto/tópico é: "${topic}".
    
    Regras OBRIGATÓRIAS:
    1. NÃO inclua introduções como "Aqui está uma sugestão", "Assunto:", ou "Prezado". Comece a mensagem diretamente.
    2. O tom deve ser de suporte/administrativo, educado e direto.
    3. Máximo de 3 parágrafos curtos.
    4. Em Português do Brasil.
    5. NÃO adicione assinatura no final (será adicionada automaticamente).`;

    const makeRequest = () => axios.post(`${API_URL}?key=${API_KEY}`, {
      contents: [{
        parts: [{
          text: prompt
        }]
      }]
    });

    const response = await retryOperation(makeRequest);

    if (
      response.data && 
      response.data.candidates && 
      response.data.candidates.length > 0 &&
      response.data.candidates[0].content &&
      response.data.candidates[0].content.parts &&
      response.data.candidates[0].content.parts.length > 0
    ) {
      let generatedText = response.data.candidates[0].content.parts[0].text.trim();
      
      // Remove possible subject line if AI ignored instructions
      generatedText = generatedText.replace(/^Assunto:.*\n+/i, '');
      generatedText = generatedText.replace(/^Subject:.*\n+/i, '');
      
      // Ensure proper salutation
      const salutation = `Prezado(a) ${recipientName || 'Usuário'},`;
      if (!generatedText.startsWith('Prezado')) {
        generatedText = `${salutation}\n\n${generatedText}`;
      }

      // Append the specific signature
      const signature = `\n\nAtenciosamente,\n\nLuiz\nCriaApp.`;
      
      // Remove any existing signature the AI might have added
      generatedText = generatedText.replace(/\n\nAtenciosamente,[\s\S]*$/, '');
      
      return generatedText + signature;
    } else {
      throw new Error('Resposta inválida da API do Gemini.');
    }
  } catch (error) {
    console.error('Erro ao gerar texto com IA:', error);
    // Fallback in case of error
    return `Erro ao gerar texto com IA. Por favor, tente novamente ou escreva manualmente.\n\nErro: ${error.message}`;
  }
};
